import React, { useRef, useState } from 'react';
import { Modal } from '@instinet-analytics/semantic-ui-components';
import ActiveFilters from './ActiveFilters';
import { MembershipSign } from 'utils/filter/filter';
import {
    FilterState,
    isColumnFilter,
    isRowFilter,
    RowFilterState,
} from 'widgets/store/grid/filters/FilterState';
import { ColumnDefinitionsDictionary } from '@instinet-analytics/semantic-ui-components';

import './FiltersModal.scss';
import {
    Comparison,
    isComparisonValid,
    toFilter,
} from 'utils/components/FiltersModal/comparison/FilterComparison';
import FilterColumnsDropdown from 'utils/components/FiltersModal/FilterColumnsDropdown';
import FilterComparisonEditor from 'utils/components/FiltersModal/FilterComparisonEditor';
import SelectedRowsEditor from 'utils/components/FiltersModal/SelectedRowsFilter';
import { PubSub } from '@instinet-analytics/core-lib';
import { EditedColumnNames } from 'grid/columnNames/hooks';

export interface FiltersModalProps<TColumnId extends string, TDataItem> {
    columnDefinitions: ColumnDefinitionsDictionary<TColumnId>;
    editedColumnNames?: EditedColumnNames;
    data?: Array<TDataItem>;
    staticData?: { [dimension: string]: string[] };
    visibleColumns: TColumnId[];
    filters: FilterState<TColumnId>[];
    selectedRowIndexes?: number[];
    trigger: React.ReactElement;
    title?: string;
    noun?: string;
    onUpdateFilters: (filters: FilterState<TColumnId>[]) => void;
    onUpdateSelectedRows?: (selectedIndexes: number[]) => void;
}

export default function FiltersModal<TColumnId extends string, TDataItem>({
    columnDefinitions,
    editedColumnNames,
    data,
    staticData,
    trigger,
    visibleColumns,
    filters,
    selectedRowIndexes,
    onUpdateFilters,
    onUpdateSelectedRows,
    title,
    noun,
}: FiltersModalProps<TColumnId, TDataItem>) {
    const [touched, setTouched] = useState(false);

    const [pendingFilters, setPendingFilters] = useState(filters);
    const [pendingComparison, setPendingComparison] = useState<
        Comparison | undefined
    >(undefined);
    const [editingColumn, setEditingColumn] = useState<TColumnId | undefined>(
        undefined
    );

    // When the modal is opened we snapshot the data for autocomplete...
    const [dataSnapshot, setDataSnapshot] = useState<Array<TDataItem>>([]);

    const pendingColumnFilters = pendingFilters.filter(isColumnFilter);

    const editingColumnHasActiveFilter = !!pendingColumnFilters.find(
        (c) => c.column === editingColumn
    );

    const isComparisonPresentAndValid = (c?: Comparison) =>
        c && isComparisonValid(c);
    const isPendingComparisonPresentAndValid =
        isComparisonPresentAndValid(pendingComparison);

    const onOpen = () => {
        setDataSnapshot(data ?? []);
        setPendingFilters(filters);
        setEditingColumn(undefined);
        setPendingComparison(undefined);
        setTouched(false);
    };

    const onAddRowFilter = (filter: RowFilterState) => {
        const newRowFilter = createNewRowFilterFromExistingFilters(
            pendingFilters,
            filter
        );

        setPendingFilters([
            ...pendingFilters.filter((x) => !isRowFilter(x)),
            newRowFilter,
        ]);
        setTouched(true);
    };

    const onDeleteFilter = (index: number) => {
        const updatedActiveFilters = [...pendingFilters];
        updatedActiveFilters.splice(index, 1);
        setPendingFilters(updatedActiveFilters);
        setEditingColumn(undefined);
        setPendingComparison(undefined);
        setTouched(true);
    };

    const onToggleEditingColumn = (column: TColumnId) => {
        setEditingColumn(editingColumn === column ? undefined : column);
    };

    const onClearAllFilters = () => {
        setPendingFilters([]);
        setTouched(true);
    };

    const onCancel = () => {
        return;
    };

    const closePubSubRef = useRef(new PubSub<void>());

    const commitAndSave = (nextPendingFilters: FilterState<TColumnId>[]) => {
        onUpdateFilters(nextPendingFilters);
        if (nextPendingFilters.map((f) => f).some(isRowFilter)) {
            if (onUpdateSelectedRows) {
                onUpdateSelectedRows([]);
            }
        }
    };

    /*
     * Commits the current edit on the left hand side to the pending active filters
     * Used by the "Add/Update" filter link, and on "Apply and Close" (to apply unsaved changes for https://jira.isn.instinet.com:8443/browse/IND2-1202)
     * The latest set of pending active filters are returned so they can be applied in "onSave"
     */
    const onCommitEditingComparison: (
        nextPendingComparison?: Comparison
    ) => FilterState<TColumnId>[] = (nextPendingComparison) => {
        const committingComparison = nextPendingComparison ?? pendingComparison;

        if (!isComparisonPresentAndValid(committingComparison)) {
            return pendingFilters;
        }

        const filter = toFilter(committingComparison!);

        const existingPending = pendingColumnFilters.find(
            (f) => f.column === editingColumn
        );

        // Replace existing or add to end...
        const index = existingPending
            ? pendingFilters.indexOf(existingPending)
            : pendingFilters.length;

        const nextPendingFilters = [...pendingFilters];
        nextPendingFilters[index] = { column: editingColumn!, filter };

        const unchanged =
            existingPending &&
            JSON.stringify(existingPending.filter) === JSON.stringify(filter);

        if (!unchanged) {
            setPendingFilters(nextPendingFilters);
            setTouched(true);
        }

        if (unchanged && touched) {
            closePubSubRef.current.publish();
            commitAndSave(nextPendingFilters);
        }

        return nextPendingFilters;
    };

    const onSetPendingComparison = (
        nextPendingComparison: Comparison | undefined,
        commitOnSet = false
    ) => {
        setPendingComparison(nextPendingComparison);
        if (commitOnSet) {
            onCommitEditingComparison(nextPendingComparison);
        }
    };

    const onSave = () => {
        if (touched || isPendingComparisonPresentAndValid) {
            const nextPendingFilters = onCommitEditingComparison();
            commitAndSave(nextPendingFilters);
        }
    };

    return (
        <Modal
            className="FiltersModal"
            trigger={trigger}
            headerText={title ?? 'Column Filters'}
            saveText="Apply and Close"
            onOpen={onOpen}
            onCancel={onCancel}
            onSave={onSave}
            closePubSub={closePubSubRef.current}
        >
            <div className="filters-modal-content">
                <div className="filter-editor">
                    <div className="title">
                        {editingColumnHasActiveFilter ? 'Edit' : 'Add'} a{' '}
                        {noun ?? 'filter'}
                    </div>
                    {!editingColumnHasActiveFilter && selectedRowIndexes && (
                        <SelectedRowsEditor
                            selectedRows={selectedRowIndexes}
                            onAddRowFilter={onAddRowFilter}
                        />
                    )}
                    <FilterColumnsDropdown
                        columnDefinitions={columnDefinitions}
                        editedColumnNames={editedColumnNames}
                        visibleColumns={visibleColumns}
                        editingColumn={editingColumn}
                        setEditingColumn={setEditingColumn}
                    />
                    <FilterComparisonEditor
                        columnDefinitions={columnDefinitions}
                        editedColumnNames={editedColumnNames}
                        data={dataSnapshot}
                        noun={noun}
                        staticData={staticData}
                        pendingColumnFilters={pendingColumnFilters}
                        pendingComparison={pendingComparison}
                        isPendingComparisonInvalid={
                            !isPendingComparisonPresentAndValid
                        }
                        editingColumn={editingColumn}
                        onSetPendingComparison={onSetPendingComparison}
                        onCommitEditingComparison={onCommitEditingComparison}
                    />
                </div>

                <ActiveFilters
                    columnDefinitions={columnDefinitions}
                    editingColumn={editingColumn}
                    editedColumnNames={editedColumnNames}
                    pendingFilters={pendingFilters}
                    noun={noun}
                    onToggleEditingColumn={onToggleEditingColumn}
                    onClearAllFilters={onClearAllFilters}
                    onDeleteFilter={onDeleteFilter}
                />
            </div>
        </Modal>
    );
}

function createNewRowFilterFromExistingFilters<TColumnId extends string>(
    existingFilters: FilterState<TColumnId>[],
    rowFilter: RowFilterState
): RowFilterState {
    const existingRowFilter = existingFilters.find((x) => isRowFilter(x)) as
        | RowFilterState
        | undefined;

    if (rowFilter.sign !== MembershipSign.INCLUDE && existingRowFilter) {
        if (existingRowFilter.sign === MembershipSign.INCLUDE) {
            return {
                sign: MembershipSign.INCLUDE,
                rowIds: existingRowFilter.rowIds.filter(
                    (x) => !rowFilter.rowIds.includes(x)
                ),
            };
        } else {
            return {
                sign: MembershipSign.EXCLUDE,
                rowIds: [...existingRowFilter.rowIds, ...rowFilter.rowIds],
            };
        }
    }

    return rowFilter;
}
